<template>
  <div class="cumulative-chart-container">
    <div class="header-container">
      <div class="header">
        CUMULATIVE INSTALLATIONS OVER TIME
      </div>
      <div class="filter-container">
        <button
          :class="[
            'filter-button',
            activeFilter === '1week' ? 'active' : null,
          ]"
          @click="changeDateFilter('1week')"
        >
          1 week
        </button>
        <button
          :class="[
            'filter-button',
            activeFilter === '1month' ? 'active' : null,
          ]"
          @click="changeDateFilter('1month')"
        >
          1 month
        </button>
        <button
          :class="[
            'filter-button',
            activeFilter === '6months' ? 'active' : null,
          ]"
          @click="changeDateFilter('6months')"
        >
          6 months
        </button>
        <button
          :class="[
            'filter-button',
            activeFilter === '1year' ? 'active' : null,
          ]"
          @click="changeDateFilter('1year')"
        >
          1 year
        </button>
        <button
          :class="[
            'filter-button',
            activeFilter === 'allTime' ? 'active' : null,
          ]"
          @click="changeDateFilter('allTime')"
        >
          All Time
        </button>
        <Datepicker
          v-model="dateRange"
          mode="range"
          :max-date="new Date()"
          popover-align="right"
        >
          <template #default>
            <button
              :class="[
                'filter-button',
                activeFilter === 'custom' ? 'active' : null,
              ]"
            >
              <div v-if="activeFilter !== 'custom'">
                Custom
                <img
                  src="@shared/assets/img/general/chevron-grey-down.png"
                  alt="Select"
                  class="button-icon"
                >
              </div>
              <span v-else>
                {{ customRangeHeader }}
              </span>
              <div
                v-if="activeFilter === 'custom' && dateRange.start && dateRange.end"
                class="clear-container"
              >
                <div class="clear-tooltip">
                  Clear Dates
                </div>
                <button
                  class="clear-btn"
                  @click="clearRangeFilter"
                >
                  <img
                    class="button-icon"
                    src="@shared/assets/img/general/x-white.png"
                    alt="Clear"
                  >
                </button>
              </div>
            </button>
          </template>
        </Datepicker>
      </div>
    </div>
    <div
      v-if="loading"
      class="loading-container"
    >
      <img
        src="@shared/assets/img/general/loading-white.svg"
        alt="Loading"
        class="loader-img"
      >
    </div>
    <highcharts
      v-else
      :options="chartOptions"
    />
    <div
      v-if="!loading && isThereData"
      class="no-data"
    >
      No Installations during this period
    </div>
    <div class="chart-legend-container">
      <div class="chart-legend-item">
        <div
          class="chart-legend-symbol"
          :style="{ backgroundColor: '#17244B' }"
        />
        <div class="chart-item-symbol">
          Installations
        </div>
      </div>
      <div class="chart-legend-item">
        <div
          class="chart-legend-symbol"
          :style="{ backgroundColor: '#4C5781' }"
        />
        <div class="chart-item-symbol">
          Verified Systems
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Datepicker from 'v-calendar/lib/components/date-picker.umd';

import adoptionMixin from '@shared/mixins/adoptionMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import adminMixin from '@shared/mixins/adminMixin';

import apiClient from '@shared/lib/general/apiClient';

export default {
  name: 'CumulativeChart',
  components: {
    Datepicker,
  },
  mixins: [adoptionMixin, adminMixin, unitPreferencesMixin],
  data() {
    return {
      activeFilter: '1year',
      graphData: [],
      customRange: {
        start: null,
        end: null,
      },
      loading: false,
    };
  },
  computed: {
    dateRange: {
      get() {
        return this.customRange;
      },
      set(dateRange) {
        if (dateRange && dateRange.start && dateRange.end) {
          this.customRange = dateRange;
          this.activeFilter = 'custom';
          this.getCumulativeInstallGraph();
        }
      },
    },
    graphValues() {
      if (this.activeFilter === '1month') {
        const weekData = {};
        this.graphData.forEach(({
          date, count, verifiedCount, week,
        }) => {
          if (!weekData[week]) {
            weekData[week] = {
              date,
              counts: [count],
              verifiedCounts: [verifiedCount],
            };
          } else if (weekData[week]) {
            weekData[week].counts.push(count);
            weekData[week].verifiedCounts.push(verifiedCount);
          }
        });
        return Object.values(weekData).map(({ date, verifiedCounts, counts }) => ({
          date,
          counts: Math.max(...counts),
          verifiedCounts: Math.max(...verifiedCounts),
        }));
      }
      if (this.activeFilter === '6months'
        || this.activeFilter === '1year') {
        const monthData = {};
        this.graphData.forEach(({
          date, verifiedCount, count, month, year,
        }) => {
          const key = `${month}-${year}`;
          if (!monthData[key]) {
            monthData[key] = {
              date,
              counts: [count],
              verifiedCounts: [verifiedCount],
            };
          } else if (monthData[key]) {
            monthData[key].date = date;
            monthData[key].counts.push(count);
            monthData[key].verifiedCounts.push(verifiedCount);
          }
        });
        const data = Object.values(monthData).map(({ date, verifiedCounts, counts }) => ({
          date,
          counts: Math.max(...counts),
          verifiedCounts: Math.max(...verifiedCounts),
        }));
        data.sort((a, b) => new Date(a.date) - new Date(b.date));
        return data;
      }
      if (this.activeFilter === 'allTime') {
        const yearData = {};
        this.graphData.forEach(({ count, verifiedCount, year }) => {
          if (!yearData[year]) {
            yearData[year] = {
              date: year,
              counts: [count],
              verifiedCounts: [verifiedCount],
            };
          } else if (yearData[year]) {
            yearData[year].counts.push(count);
            yearData[year].verifiedCounts.push(verifiedCount);
          }
        });

        const data = Object.values(yearData).map(({ date, counts, verifiedCounts }) => ({
          date,
          counts: Math.max(...counts),
          verifiedCounts: Math.max(...verifiedCounts),
        }));
        return this.checkYearData(data);
      }
      return this.graphData.map(({ verifiedCount, count, ...rest }) => ({
        ...rest,
        counts: count,
        verifiedCounts: verifiedCount,
      }));
    },
    xAxis() {
      return this.graphValues.map(({ date }, idx) => {
        if (idx === 0 && this.activeFilter === '1month') {
          const { date: nextDate } = this.graphValues[1];
          return moment(nextDate).subtract(1, 'w').format('MM-DD-YYYY');
        }
        if (typeof date === 'number') {
          return `${date}-01-01`;
        }
        return date;
      });
    },
    yAxis() {
      return this.graphValues.map(({ counts }) => counts);
    },
    vyAxis() {
      return this.graphValues.map(({ verifiedCounts }) => verifiedCounts);
    },
    chartOptions() {
      const {
        activeFilter,
        dateFormat,
        dateAndYearFormat,
      } = this;

      return {
        chart: {
          zoomType: 'x',
          height: 300,
          backgroundColor: 'transparent',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        tooltip: {
          useHTML: true,
          nullFormat: 'Value is not available.',
          formatter() {
            let date = moment(this.x).format(dateAndYearFormat);
            if (activeFilter === '1month') {
              const startDate = moment(this.x).subtract(6, 'd').format(dateAndYearFormat);
              const endDate = moment(this.x).format(dateAndYearFormat);
              date = `${startDate} to ${endDate}`;
            } else if (activeFilter === '6months' || activeFilter === '1year') {
              const startDate = moment(this.x).startOf('month').format(dateAndYearFormat);
              let endDate = moment(this.x).endOf('month').format(dateAndYearFormat);
              if (moment(this.x).endOf('month').isAfter(moment())) {
                endDate = moment().format(dateAndYearFormat);
              }
              date = `${startDate} to ${endDate}`;
            } else if (activeFilter === 'allTime') {
              const topOfYear = moment(this.x).format(dateAndYearFormat);
              const currentYear = moment().format('YYYY');
              let endOfTheYear = '';
              if (this.x.split('-')[0] === currentYear) {
                endOfTheYear = moment().format(dateAndYearFormat);
              } else {
                endOfTheYear = moment(this.x).endOf('year').format(dateAndYearFormat);
              }
              date = `${topOfYear} to ${endOfTheYear}`;
            }
            return `${date}<br/><b>${this.y} unit${this.y > 1 ? 's' : ''}</b>`;
          },
          style: {
            fontFamily: 'Poppins',
            // zIndex: 100,
          },
        },
        xAxis: [{
          categories: this.xAxis,
          title: {
            text: '',
          },
          labels: {
            style: {
              color: 'white',
              fontFamily: 'Poppins',
            },
            formatter() {
              if (activeFilter !== 'allTime') {
                return moment(this.value).format(`${dateFormat}`);
              }
              return moment(this.value).format('YYYY');
            },
          },
          gridLineColor: '#121830',
        },
        ],
        yAxis: {
          labels: {
            style: {
              color: 'white',
              fontFamily: 'Poppins',
            },
          },
          title: {
            text: '',
          },
          tickInterval: 10,
          min: Math.min(...this.vyAxis) <= 10 ? 0 : Math.min(...this.vyAxis) - 10,
          max: Math.max(...this.yAxis) + 200,
          gridLineColor: '#121830',
          // gridLineColor: 'transparent',
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          areaspline: {
            pointPlacement: 'on',
            fillColor: 'rgba(73, 121, 255, 0.2)',
            lineColor: '#828282',
            marker: {
              fillColor: '#0B0F1E',
              lineColor: '#FFFFFF',
              lineWidth: 3,
            },
          },
        },
        series: [{
          type: 'areaspline',
          name: 'Installation',
          legendSymbol: 'square',
          data: this.yAxis,
        },
        {
          type: 'areaspline',
          name: 'Verified Systems',
          legendSymbol: 'square',
          data: this.vyAxis,
          pointPlacement: 'on',
          fillColor: '#7079A6',
          marker: {
            symbol: 'circle',
            fillColor: '#0B0F1E',
            lineColor: '#FFFFFF',
            lineWidth: 3,
          },
        }],
      };
    },
    customRangeHeader() {
      const {
        start,
        end,
      } = this.customRange;

      const {
        dateFormat,
      } = this;

      if (start && end) {
        let startFormat = dateFormat === 'M/D' ? 'MMM D' : 'D MMM';
        if (!moment(start).isSame(end, 'year')) {
          startFormat = dateFormat === 'M/D' ? 'MMM D \'YY' : 'D MMM \'YY';
        }
        const endFormat = dateFormat === 'M/D' ? 'MMM D \'YY' : 'D MMM \'YY';
        const rangeStart = moment(start).format(startFormat);
        const rangeEnd = moment(end).format(endFormat);
        return `${rangeStart} - ${rangeEnd}`;
      }
      return null;
    },
    isThereData() {
      const sumOfYAxis = this.yAxis.reduce((a, b) => a + b, 0);
      const sumOfVYAxis = this.vyAxis.reduce((a, b) => a + b, 0);
      if ((sumOfVYAxis === this.vyAxis.length && sumOfYAxis === this.yAxis.length) || (this.yAxis.length === 0 && this.vyAxis.length === 0)) {
        return true;
      }
      return false;
    },
  },
  watch: {
    activeFilter() {
      this.getCumulativeInstallGraph();
    },
    selectedCorpId() {
      this.getCumulativeInstallGraph();
    },
  },
  mounted() {
    if (this.selectedCorpId) {
      this.getCumulativeInstallGraph();
    }
  },
  methods: {
    async getCumulativeInstallGraph() {
      const {
        start,
        end,
      } = this.customRange;
      this.loading = true;
      try {
        let queryParams = '?';
        let startQuery = null;
        let endQuery = moment().format('YYYY-MM-DD');
        if (this.activeFilter === '1week') {
          startQuery = moment().subtract(7, 'days').format('YYYY-MM-DD');
        } else if (this.activeFilter === '1month') {
          startQuery = moment().subtract(30, 'days').format('YYYY-MM-DD');
        } else if (this.activeFilter === '6months') {
          startQuery = moment().subtract(180, 'days').format('YYYY-MM-DD');
        } else if (this.activeFilter === '1year') {
          startQuery = moment().subtract(364, 'days').format('YYYY-MM-DD');
        } else if (this.activeFilter === 'custom') {
          startQuery = moment(start).format('YYYY-MM-DD');
          endQuery = moment(end).format('YYYY-MM-DD');
        }
        if (startQuery) {
          queryParams += `start=${startQuery}&`;
        }
        queryParams += `end=${endQuery}`;
        let url = `/adoption/stat/cumulative/install/corp/${this.selectedCorpId}`;
        url += queryParams;
        const { data } = await apiClient({
          method: 'GET',
          url,
        });
        this.graphData = data;
      } catch (err) {
        console.error(err);
      }
      this.loading = false;
    },
    clearRangeFilter() {
      this.customRange = {
        start: null,
        end: null,
      };
      this.activeFilter = '1year';
    },
    changeDateFilter(date) {
      this.activeFilter = date;
      if (date !== 'custom') {
        this.customRange = {
          start: null,
          end: null,
        };
      }
    },
    checkYearData(yearData) {
      // eventually all date ranges?
      if (yearData.length === 5 || yearData.length === 0) {
        return yearData;
      }
      const allYears = [];
      const yearsInData = [];
      const missingYears = [];
      for (let i = 0; i <= 4; i++) {
        allYears.push(moment().year() - i);
      }
      yearData.forEach((year) => yearsInData.push(year.date));
      allYears.reverse();
      allYears.forEach((year, idx) => {
        if (!yearsInData.includes(year)) {
          missingYears.push({
            date: year,
            index: idx,
          });
        }
      });
      missingYears.forEach((year) => {
        const missingItem = {
          date: year.date,
          counts: 0,
          verifiedCounts: 0,
        };
        yearData.splice(year.index, 0, missingItem);
      });
      return yearData;
    },
  },
};
</script>

<style lang="scss" scoped>
.cumulative-chart-container {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: 34px;
  .header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    .header {
      font-size: 0.875rem;
      font-weight: 700;
      color: $WHITE;
      white-space: nowrap;
    }
  }
  .no-data {
    position: absolute;
    z-index: 1;
    top: 45%;
    left: 25%;
    color: $WHITE;
    font-size: 20px;
    font-weight: bold;
  }
  .chart-legend-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    .chart-legend-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      &:first-child {
        margin-right: 15px;
      }
      .chart-legend-symbol {
        border: 1px solid #8990A2;
        border-radius: 8px;
        width: 25px;
        height: 25px;
        margin-right: 10px;
      }
      .chart-item-symbol {
        color: $WHITE;
        font-size: 12px;
        font-weight: bold;
      }
    }
  }
  .filter-container {
    display: flex;

    .filter-button {
      font-size: 0.875rem;
      font-weight: 500;
      color: $N60;
      text-decoration: underline;
      margin-left: 24px;
      display: flex;
      white-space: nowrap;
      align-items: center;

      &.active {
        font-weight: 700;
        color: $WHITE;
        text-decoration: none;
      }

      .button-icon {
        width: 12px;
        margin-left: 5px;
        object-fit: contain;
      }

      .clear-container {
        position: relative;

        &:hover {
          .clear-tooltip {
            opacity: 1;
          }
        }

        .clear-btn {
          display: flex;
        }

        .clear-tooltip {
          position: absolute;
          top: -20px;
          left: -26px;
          opacity: 0;
          background-color: $WHITE;
          color: $N100;
          font-size: 0.625rem;
          padding: 2px 4px;
          border-radius: 4px;
          white-space: nowrap;

          &:hover {
            opacity: 1;
          }
        }
      }
    }
  }

  .loading-container {
    height: 300px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
